<template>
  <div class="dynamic-field-container">
    <h6>Price Details</h6>
    <div style="border: 1px dashed #aaa; padding: 12px;">
      <div class="el-input">
        <label>Price Rule
          <span  v-if="displayPriceRuleToolTip">
          <el-tooltip effect="dark" placement="top" style="padding: 1px 5px; margin-top: -1px;">
                <div slot="content">
                  <p>Select a Price Rule</p>
                </div>
                <i class="nc-icon nc-alert-circle-i"></i>`
          </el-tooltip>
        </span>
        </label>
        <el-select
          clearable
          style="width: 95%"
          class="fifty-percent dynamic-field-container"
          v-model="fieldValues.priceRuleId"
          @change="setQuickbooksDataAndTravelCharge"
          :disabled="isTaskFieldDisabled"
          no-data-text="No Price Rule Match. Enter a Price"
          placeholder="Price Rule">
          <el-option v-for="priceRule in priceRules"
                     class="select price-rule-option"
                     :label="getPriceRuleLabel(priceRule)"
                     :value="priceRule.id"
                     :key="priceRule.id">
          <span>
            {{ getPriceRuleLabel(priceRule) }}
            <ul v-if="priceRule.shortId">
              <li>Short ID: {{priceRule.shortId}}</li>
            </ul>
            <ul v-if="priceRule.travelCharge">
              <li>Travel charge <i class="nc-icon nc-money-coins"></i></li>
            </ul>
            <ul v-if="priceRule.reinspection">
              <li>Reinspection <i class="nc-icon nc-ruler-pencil"></i></li>
            </ul>
            <ul v-if="priceRule.matchedFields">
              <li v-for="(value, name) in priceRule.matchedFields">{{ ucFirst(name) }} : {{ value }}</li>
            </ul>
          </span>
          </el-option>
        </el-select>
      </div>
      <div class="el-input inline-block" style="max-width: 95%">
        <label>Price</label>
        <vue-numeric
          currency="$"
          separator=","
          :precision="2"
          :minus="false"
          name="price"
          class="fifty-percent el-input__inner dynamic-field-container"
          v-model="fieldValues.price"
          style="width: 120px;"
          :disabled="isTaskFieldDisabled"
          @blur="changePrice"
          placeholder="$0.00"></vue-numeric>
        <span v-if="tripCharge" style="margin-left: 8px; padding: 5px; background-color: wheat; border-radius: 3px;"><i class="nc-icon nc-money-coins"></i><span style="padding-left: 5px;">TRIP CHARGE</span></span>
        <small class="text-danger" v-show="errors.has('price')" style="display: block; margin-top: 3px;">
          {{ getError('price') }}
        </small>
      </div>
      <div v-if="hourly && !tripCharge" style="width: 95%; margin-bottom: 6px; padding: 5px; background-color: wheat; border-radius: 3px;"><i class="nc-icon nc-money-coins"></i><span style="padding-left: 5px;">HOURLY CHARGE: {{hourlyLabel}}</span></div>
      <div class="inline-block" v-if="fieldValues.poPrice">
        <label>Requested Price from P.O.</label>
        <div>${{ fieldValues.poPrice }}</div>
      </div>
      <div class="el-input">
        <label>Travel Charge</label>
        <vue-numeric
          currency="$"
          separator=","
          :precision="2"
          :minus="false"
          name="travelCharge"
          class="fifty-percent el-input__inner"
          v-model="fieldValues.travelCharge"
          style="width: 120px;"
          :disabled="isTaskFieldDisabled"
          @blur="updateTravelChargeInVuex"
          placeholder="$0.00"></vue-numeric>
      </div>
      <h5 style="margin: 15px 0 8px 0;">Quickbooks</h5>
      <div class="el-input">
        <label>Item Code</label>
        <el-select
          class="dynamic-field-container"
          style="width: 75%"
          clearable
          name="qbItemCode"
          v-model="fieldValues.qbItemCode"
          v-validate="fieldValidationRules"
          data-vv-name="qbItemCode"
          data-vv-as="Quickbooks Item Code"
          :disabled="isTaskFieldDisabled"
          placeholder="Quickbooks Item Code">
          <el-option v-for="itemCode in itemCodes"
                     class="select"
                     :label="itemCode.label"
                     :value="itemCode.label"
                     :key="itemCode.value">
          </el-option>
        </el-select>
        <small class="text-danger" v-show="errors.has('qbItemCode')" style="display:block; margin: -6px 0 6px 0;">
          {{ getError('qbItemCode') }}
        </small>
      </div>
      <div class="el-input">
        <label>Class</label>
        <el-select
          class="dynamic-field-container"
          style="width: 75%"
          clearable
          v-model="fieldValues.qbClass"
          name="qbClass"
          data-vv-name="qbClass"
          v-validate="fieldValidationRules"
          data-vv-as="Quickbooks Class"
          :disabled="isTaskFieldDisabled"
          placeholder="Quickbooks Class">
          <el-option v-for="qbClass in constants.QUICKBOOKS.CLASSES"
                     class="select"
                     :label="qbClass"
                     :value="qbClass"
                     :key="qbClass">
          </el-option>
        </el-select>
        <small class="text-danger" v-show="errors.has('qbClass')" style="display:block; margin: -6px 0 6px 0;">
          {{ getError('qbClass') }}
        </small>
      </div>
      <div class="el-input">
        <label>Item Description</label>
        <el-input
          name="qbItemDescription"
          clearable
          autosize
          type="textarea"
          v-model="fieldValues.qbItemDescription"
          v-validate="fieldValidationRules"
          data-vv-name="qbItemDescription"
          data-vv-as="Quickbooks Item Description"
          :disabled="isTaskFieldDisabled"
          placeholder="Quickbooks Item Description" />
        <small class="text-danger" v-show="errors.has('qbItemDescription')" style="display:block;">
          {{ getError('qbItemDescription') }}
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import {
  formatCurrency,
  objectById,
  notifyError,
  notifyWarning,
  maybeAppendCents,
  canEditTaskField,
  ucFirst,
  kvpsForSelectTheWorks,
  roundAccurately
} from 'src/utilities/utils'
import {mapGetters} from 'vuex'
import VueNumeric from 'vue-numeric'
import Constants from 'src/utilities/constants'
import {BAD_REQUEST, OK, NOT_FOUND} from 'http-status-codes'
import _ from 'lodash'

export default {
  inject: ['$validator'],
  props: ['field', 'value', 'commit', 'task', 'disabled'],
  fieldName: 'temp',
  components: {VueNumeric},
  computed: {
    ...mapGetters({
      priceRules: 'priceRules/results',
      tripCharge: 'priceRules/tripCharge',
      tasksByJobId: 'tasks/byJobId',
      jobId: 'tasks/edit/jobId',
      allItemCodes: 'quickbooks/results'
    }),
    fieldValidationRules() {
      // expressions override other validation logic
      if (this.field.expression) {
        this.fieldValidation = function(str) {
          return eval(str).apply(this)
        }.call(this, this.field.expression)
      }
      return this.fieldValidation
    },
    isTaskFieldDisabled() {
      if (this.disabled) {
        return true
      }
      return !canEditTaskField(this)
    },
    itemCodes() {
      const departmentId = this.currentJob.departmentId
      if (departmentId) {
        const filteredICs = _.filter(this.allItemCodes, (itemCode) => itemCode.departmentId === departmentId)
        return kvpsForSelectTheWorks(filteredICs, {
          sortBy: 'name',
          labelProp: 'name',
          valueProp: 'id'
        })
      }
      return []
    },
    displayPriceRuleToolTip() {
      return this.priceRules.length > 1 && !this.fieldValues.priceRuleId
    }
  },
  data() {
    return {
      constants: Constants,
      fieldValidation: {},
      hourly: false,
      hourlyLabel: '',
      currentJob: {},
      statusOnLoad: null,
      travelChargeHolder: 0,
      fieldValues: {
        customPrice: false,
        poNumber: null,
        priceRuleId: null,
        price: 0,
        poPrice: null,
        qbItemCode: null,
        qbClass: null,
        qbItemDescription: null,
        travelCharge: 0
      }
    }
  },
  methods: {
    ucFirst(prop) {
      return ucFirst(prop)
    },
    getError(fieldName) {
      return this.errors.first(fieldName)
    },
    getPriceRuleLabel(priceRule) {
      let label = formatCurrency(priceRule.price)
      if (priceRule.clientDefault) {
        label += ' - Client Default'
      } else if (priceRule.projectDefault) {
        label += ' - Project Default'
      } else {
        priceRule.taskName ? label += ' - Task Name: ' + priceRule.taskName : label += ' - Job Name: ' + priceRule.jobName
      }
      return label
    },
    async fetchJob() {
      const response = await this.$store.dispatch('fetchById', {
        endpoint: 'jobs',
        id: this.task.jobId
      })
      if (OK !== response.status) {
        notifyError(this, "Failed to load Job by ID: " + this.task.jobId)
        return
      }
      this.currentJob = response.data
    },
    async fetchPriceRules() {
      const payload = {
        job: this.currentJob
      }

      if (this.currentJob.invoiceBy === Constants.INVOICE_BY.MULTIPLE_TASKS) {
        payload.taskId = this.$props.task.id
      }

      const response = await this.$store.dispatch('priceRules/fetchPriceRules', payload)

      if (NOT_FOUND === response.status) {
        notifyWarning(this, 'No matching price rules found. Please enter price details manually.')
        return
      } else if (BAD_REQUEST === response.status) {
        notifyError(this, response.data.message)
        return
      } else if (OK !== response.status) {
        notifyError(this, 'Error Loading Price Rules')
        return
      }

      let priceRule = null

      if (this.task.fieldValues.priceRuleId) {
        priceRule = objectById(this.priceRules, this.task.fieldValues.priceRuleId)
      } else if ((this.tripCharge || this.priceRules.length === 1) && this.fieldValues.customPrice === false) {
        priceRule = this.priceRules[0]
      }

      // only set QB data if a price rule hasn't been selected before
      if (priceRule) {
        this.setQuickbooksData(priceRule.id, false)
      }
    },
    maybeShowHourlyLabel(priceRule) {
      this.hourly = false
      if (priceRule.hourly) {
        this.hourly = priceRule.hourly
        if (_.get(this.$props.task, 'fieldValues.hoursWorked') > 1) {
          const hoursGreaterThanOne = this.getHoursGreaterThanOne()
          this.hourlyLabel = '$' + maybeAppendCents(priceRule.price) + ' + (' + roundAccurately(hoursGreaterThanOne, 2) + ' * $' + maybeAppendCents(priceRule.hourlyRate) + ')'
        } else {
          this.hourlyLabel = '$' + maybeAppendCents(Number(priceRule.price))
        }
      }
    },
    getHoursGreaterThanOne() {
      return this.$props.task.fieldValues.hoursWorked - 1
    },
    setPrice(priceRule) {
      let totalPrice = Number(priceRule.price)
      if (this.hourly && _.get(this.$props.task, 'fieldValues.hoursWorked') > 1) {
        const hourlyTotal = priceRule.hourlyRate * this.getHoursGreaterThanOne()
        totalPrice += hourlyTotal
      }
      this.fieldValues.price = totalPrice //maybeAppendCents(totalPrice)
      this.$store.commit('tasks/edit/editFieldValue', {key: 'price', value: this.fieldValues.price})
    },
    setQuickbooksDataAndTravelCharge(priceRuleId) {
      this.setQuickbooksData(priceRuleId, true)
    },
    setQuickbooksData(priceRuleId, fetchTravelCharge) {
      if (!priceRuleId) {
        return
      }

      this.$validator.reset()

      const priceRule = objectById(this.priceRules, priceRuleId)

      if (!priceRule) {
        console.error('Unable to load price rule by ID: ' + priceRuleId)
        return
      }

      this.maybeShowHourlyLabel(priceRule)
      this.setPrice(priceRule)

      this.fieldValues.priceRuleId = priceRule.id
      if (fetchTravelCharge) {
        this.setTravelCharge(priceRule)
      }
    },
    setTravelCharge(priceRule) {
      this.fieldValues.travelCharge = 0
      if (priceRule.travelCharge) {
        this.fieldValues.travelCharge = this.travelChargeHolder || 0
      }
      this.updateTravelChargeInVuex()
    },
    maybePopulateVirtualDynamicFields() {
      if (this.task.fieldValues) {
        const fv = this.task.fieldValues
        if (fv.poNumber) {
          this.fieldValues.poNumber = fv.poNumber
        }
        if (fv.priceRuleId) {
          this.fieldValues.priceRuleId = fv.priceRuleId
        }
        this.fieldValues.price = fv.price || 0
        if (fv.poPrice) {
          this.fieldValues.poPrice = fv.poPrice
        }
        this.fieldValues.travelCharge = fv.travelCharge || 0
        if (fv.qbItemCode) {
          this.fieldValues.qbItemCode = fv.qbItemCode
        }
        if (fv.qbClass) {
          this.fieldValues.qbClass = fv.qbClass
        }
        if (fv.qbItemDescription) {
          this.fieldValues.qbItemDescription = fv.qbItemDescription
        }
      }
      this.$store.commit('tasks/edit/editFieldValue', {key: 'qbArAccount', value: Constants.QUICKBOOKS.AR_ACCOUNT})
    },
    async fetchAndInit() {
      this.statusOnLoad = JSON.parse(JSON.stringify(this.task.status))
      this.maybePopulateVirtualDynamicFields()
      this.updatePriceInVuex() // default to 0 to start
      await this.fetchJob()
      const fieldValues = this.$store.getters['tasks/edit/fieldValues']
      if (fieldValues.customPrice === true) {
        this.fieldValues.customPrice = true
        this.fieldValues.travelCharge = fieldValues.travelCharge
      }
      await this.fetchPriceRules()
      await this.fetchTravelCharge()
    },
    setCustomPrice(isCustomPrice) {
      this.$store.commit('tasks/edit/editFieldValue', {key: 'customPrice', value: isCustomPrice})
      this.fieldValues.customPrice = isCustomPrice
    },
    async fetchTravelCharge() {
      if (this.task.status === Constants.STATUSES.COMPLETED
        || this.task.status === Constants.STATUSES.CANCELLED) {
        return
      }
      if (this.task.fieldValues.travelCharge > 0) {
        console.log('Travel Charge already set - not resetting')
        return
      }
      if (!this.currentJob.id) {
        console.error('this.currentJob is not loaded - unable to fetch travel charge')
        return
      }
      if (!this.currentJob.clientId) {
        return
      }

      if (this.fieldValues.customPrice === false) {
        const response = await this.$store.dispatch('jobs/fetchTravelCharge', this.currentJob)

        // states
        // - TRAVEL_CHARGE
        // - TRAVEL_CHARGE_ZERO
        // - PRICE_RULE_UNDECIDED
        // - ERROR

        if (OK === response.status) {
          if (response.data.travelChargeState === Constants.TRAVEL_CHARGE.STATES.UNDECIDED) {
            this.travelChargeHolder = response.data.travelCharge
          } else {
            this.fieldValues.travelCharge = response.data.travelCharge
            this.updateTravelChargeInVuex()
          }
        } else {
          notifyError(this, 'Error Fetching Travel Charge')
        }
      }
    },
    maybeCalculateHourlyCharge() {
      if (this.fieldValues.priceRuleId) {
        this.setQuickbooksData(this.fieldValues.priceRuleId, false)
      }
    },
    changePrice() {
      if (!this.fieldValues.price) {
        this.fieldValues.price = 0
      }
      if (this.fieldValues.price !== this.$store.getters['tasks/edit/fieldValues'].price ) {
        this.$store.commit('tasks/edit/editFieldValue', {key: 'price', value: this.fieldValues.price})
        this.setCustomPrice(true)
        this.fieldValues.priceRuleId = null
        this.$store.commit('tasks/edit/editFieldValue', {key: 'priceRuleId', value: this.fieldValues.priceRuleId})
      }
    },
    updatePriceInVuex() {
      if (!this.fieldValues.price) {
        this.fieldValues.price = 0
      }
      this.$store.commit('tasks/edit/editFieldValue', {key: 'price', value: this.fieldValues.price})
    },
    updateTravelChargeInVuex() {
      this.$store.commit('tasks/edit/editFieldValue', {key: 'travelCharge', value: this.fieldValues.travelCharge})
    }
  },
  beforeCreate() {
    this.$store.cache.dispatch('quickbooks/fetchAll')
  },
  async beforeMount() {
    this.$eventHub.$on('jobSaved', this.fetchAndInit)
    this.$eventHub.$on('timeTrackingChanged', this.maybeCalculateHourlyCharge)
    await this.fetchAndInit()
  },
  beforeDestroy() {
    this.$eventHub.$off('jobSaved')
    this.$eventHub.$off('timeTrackingChanged')
    this.$store.commit('priceRules/resetResults')
  },
  watch: { // NOTE - don't use watcher for price - vue-numeric sets value to 0 when initializing which causes a world of pain
    'fieldValues.poNumber': {
      handler: function (poNumber) {
        this.$store.commit('tasks/edit/editFieldValue', {key: 'poNumber', value: poNumber})
      }
    },
    'fieldValues.priceRuleId': {
      handler: function (priceRuleId) {
        this.$store.commit('tasks/edit/editFieldValue', {key: 'priceRuleId', value: priceRuleId})
        if (this.fieldValues.priceRuleId) {
          this.setCustomPrice(false)
        }
        if (!priceRuleId) {
          this.hourly = false
          this.hourlyLabel = null
          this.fieldValues.travelCharge = 0
          this.updateTravelChargeInVuex()
          if (!this.fieldValues.customPrice) {
            this.fieldValues.price = 0
          }
          this.setCustomPrice(true)
          this.updatePriceInVuex()
        }
      }
    },
    'fieldValues.qbItemCode': {
      handler: function (qbItemCode) {
        this.$store.commit('tasks/edit/editFieldValue', {key: 'qbItemCode', value: qbItemCode})
      }
    },
    'fieldValues.qbClass': {
      handler: function (qbClass) {
        this.$store.commit('tasks/edit/editFieldValue', {key: 'qbClass', value: qbClass})
      }
    },
    'fieldValues.qbItemDescription': {
      handler: function (qbItemDescription) {
        this.$store.commit('tasks/edit/editFieldValue', {key: 'qbItemDescription', value: qbItemDescription})
      }
    }
  }
}
</script>

<style scoped>
  label {
    display: block;
  }
  .dynamic-field-container {
    margin-bottom: 10px;
  }
  .fifty-percent {
    width: 50%
  }
</style>
